import type { ApiClient, AuthTokens } from '@dorixona/api'
import { parseTokens } from '@dorixona/api'

/** Кто вошёл: ответ `GET /admin/me` (схема Staff). */
export interface AdminMe {
  id: number
  name: string
  phone: string
  email: string
  roleId: number
  /** Название роли для подписи под именем. */
  roleName: string
  /** Права по разделам: `{ "orders": "ru" }` (docs/frontend/README.md §Admin). */
  permissions: Record<string, string>
  /** 0 — вся сеть; иначе сотрудник работает только в этой аптеке (R-08). */
  storeId: number
  /** Владелец: его нельзя отключить, удалить и сменить ему роль. */
  isSystem: boolean
  mustChangePassword: boolean
}

export interface LoginResult {
  tokens: AuthTokens
  /** Временный пароль: до смены любой другой запрос вернёт 401. */
  mustChangePassword: boolean
}

export interface AdminAuthApi {
  login(login: string, password: string): Promise<LoginResult>
  changePassword(oldPassword: string, newPassword: string): Promise<AuthTokens>
  me(): Promise<AdminMe>
  logout(refreshToken: string): Promise<void>
  /**
   * Забыть браузер для push при выходе (`DELETE /admin/push/tokens`). Токен
   * доступа передаём явно: к этому моменту сессию в панели уже сбросили, а без
   * него сервер браузер не отвяжет — и следующий сотрудник за этим компьютером
   * получал бы уведомления прежнего.
   */
  unregisterPushToken(token: string, accessToken: string): Promise<void>
}

interface StaffDto {
  id?: number
  name?: string
  phone?: string
  email?: string | null
  role_id?: number
  role?: string
  permissions?: Record<string, string>
  store_id?: number | null
  is_system?: boolean
  must_change_password?: boolean
}

function toMe(dto: StaffDto): AdminMe {
  return {
    id: dto.id ?? 0,
    name: dto.name ?? '',
    phone: dto.phone ?? '',
    email: dto.email ?? '',
    roleId: dto.role_id ?? 0,
    roleName: dto.role ?? '',
    permissions: dto.permissions ?? {},
    // null в ответе означает «вся сеть»; внутри админки это 0.
    storeId: dto.store_id ?? 0,
    isSystem: dto.is_system ?? false,
    mustChangePassword: dto.must_change_password ?? false,
  }
}

export function createAdminAuthApi(api: ApiClient): AdminAuthApi {
  return {
    async login(login, password) {
      const dto = await api.post<{ must_change_password?: boolean }>(
        '/admin/auth/login',
        { login, password },
        { auth: false },
      )
      return { tokens: parseTokens(dto), mustChangePassword: dto.must_change_password ?? false }
    },

    async changePassword(oldPassword, newPassword) {
      const dto = await api.post<unknown>('/admin/auth/password', {
        old_password: oldPassword,
        new_password: newPassword,
      })
      return parseTokens(dto)
    },

    async me() {
      return toMe(await api.get<StaffDto>('/admin/me'))
    },

    async logout(refreshToken) {
      await api.post('/admin/auth/logout', { refresh_token: refreshToken }, { auth: false })
    },

    async unregisterPushToken(token, accessToken) {
      await api.del('/admin/push/tokens', {
        body: { token },
        auth: false,
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    },
  }
}
